import React, { useEffect, useMemo, useState } from "react";
import { LineChart, Line, ResponsiveContainer, Tooltip } from "recharts";
import api from "@/lib/api";

/**
 * Top-right sparkline on /dashboard.
 * Three thin lines with a soft glow, NO axes, NO grid, NO background —
 * just the trend of the caller's activity over the last 30 days:
 *   • properties  (daily insertions)  — orange
 *   • boards      (running total)     — blue
 *   • communities (running total)     — violet
 */
const SERIES = [
  { key: "properties",  label: "Immobili",   color: "#FF9500" },
  { key: "boards",      label: "Schede",     color: "#0A84FF" },
  { key: "communities", label: "Community",  color: "#AF52DE" },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-lg bg-slate-900/90 text-white text-[11px] px-2.5 py-1.5 shadow-lg">
      <div className="opacity-60 mb-0.5">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-1.5">
          <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: p.color }} />
          <span>{p.dataKey}: <b>{p.value}</b></span>
        </div>
      ))}
    </div>
  );
};

const MiniActivityChart = () => {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    api.get("/analytics/my-activity-trend")
      .then((r) => { if (!cancel) setSeries(r.data.series || []); })
      .catch(() => {})
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, []);

  const hasAny = useMemo(
    () => series.some((d) => d.properties || d.boards || d.communities),
    [series]
  );

  return (
    <div className="w-full max-w-[340px]" data-testid="mini-activity-chart">
      <div
        className="text-[10px] uppercase tracking-[0.3em] font-semibold text-right"
        style={{ color: "#000000", textShadow: "0 0 3px rgba(255,255,255,1), 0 0 3px rgba(255,255,255,0.9)" }}
      >
        La tua attività · 30gg
      </div>
      <div className="h-16 w-full mt-1">
        <ResponsiveContainer>
          <LineChart data={series} margin={{ top: 4, right: 2, left: 2, bottom: 0 }}>
            <defs>
              {SERIES.map((s) => (
                <filter key={s.key} id={`glow-${s.key}`} x="-30%" y="-30%" width="160%" height="160%">
                  <feGaussianBlur stdDeviation="2.6" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              ))}
            </defs>
            <Tooltip content={<CustomTooltip />} />
            {SERIES.map((s) => (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                stroke={s.color}
                strokeWidth={1.8}
                dot={false}
                isAnimationActive={false}
                filter={`url(#glow-${s.key})`}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center justify-end gap-3 mt-1">
        {SERIES.map((s) => (
          <div key={s.key} className="flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: s.color, boxShadow: `0 0 6px ${s.color}` }} />
            <span
              className="text-[10px] font-semibold"
              style={{ color: "#000000", textShadow: "0 0 3px rgba(255,255,255,1)" }}
            >
              {s.label}
            </span>
          </div>
        ))}
      </div>
      {!loading && !hasAny && (
        <div
          className="text-[10px] text-right mt-1"
          style={{ color: "#000000", textShadow: "0 0 3px rgba(255,255,255,1)" }}
        >
          Ancora nessuna attività
        </div>
      )}
    </div>
  );
};

export default MiniActivityChart;
